import { DocContent } from '../docsData';

const doc: DocContent = {
  title: 'LLM Metering',
  content: `
## Overview

The LLM Metering page shows what your AI assistant usage costs, where that spend is
going, and which conversations are driving it. This guide explains every number on the
main page and on the per-conversation detail page.

> **Note**: You must have the **Admin** role to view the LLM Metering pages. You can
> find them at **Usage Analytics → Ellie Usage** in the sidebar. (This page used to live
> at Settings → Snowflake → LLM Metering.)

## Where the Costs Come From

When you chat with the assistant, two kinds of cost are incurred:

1. **AI model cost** — every message the assistant generates consumes *tokens* (small
   chunks of text, roughly ¾ of a word each). Both the conversation the model reads and
   the response it writes count. This is captured the moment each response completes,
   so these numbers are always up to date.

2. **Warehouse compute cost** — when the assistant answers a question about your data,
   it runs real queries against your Snowflake warehouse. The compute those queries use
   is metered by Snowflake and reported back. **Snowflake's reporting runs about
   3 hours behind**, so very recent activity may briefly show a warehouse cost of $0
   before catching up.

Every dollar figure on the page is one or both of these, converted from Snowflake
credits using your contracted credit rate.

### Dollars vs. Credits

Snowflake bills in *credits*, and your contract sets what a credit costs in dollars.
The dashboard shows both: dollar figures for everyday use, and raw credit figures so
your team can cross-check against Snowflake's own billing reports, which are always in
credits.

## Choosing a Time Window

- **Last 24 hours** — a rolling window ending right now. Because warehouse reporting
  lags ~3 hours, the most recent few hours may understate warehouse cost.
- **Previous day / 7 days / 30 days** — full calendar days in your workspace's
  timezone, ending at last midnight. Today's partial data is deliberately excluded so
  you're always looking at complete days.
- **Custom range** — pick any start and end day (both included). Pick the same day
  twice to see a single day.

## The Summary Cards

- **Total threads** — how many distinct conversations had AI activity in the window.
- **Total tokens** — all tokens consumed in the window, with a breakdown:
  - *in* — text the model read fresh
  - *out* — text the model wrote
  - *cache write / cache read* — see "What is caching?" below
- **Total cost** — the headline number: AI model cost plus warehouse compute cost for
  the window.
- **Avg cost / thread** — total cost divided by the number of conversations. Background
  work (see below) is left out, since it doesn't belong to any conversation.
- **LLM token cost** — just the AI model half, split into what was spent reading input,
  writing output, and writing to / reading from the cache.
- **Warehouse compute cost** — just the data-query half: how many queries the assistant
  ran and what their compute cost.
- **Background jobs** — AI spend that isn't part of any chat. The platform also uses AI
  behind the scenes (for example, suggesting column names for new data sources). That
  spend is shown here so the total is complete, with a note of which automated task it
  came from.
- **LLM credits / Warehouse credits** — the same two halves expressed in raw Snowflake
  credits, for reconciling with Snowflake's billing reports.

### What Is Caching?

To save money, the start of a long conversation can be *cached* so the model doesn't
pay full price to re-read it on every reply. Writing to the cache costs slightly more
than normal reading (about 1.25×), but reading from it afterwards costs only about a
tenth. A conversation with lots of "cache read" tokens is running efficiently.

## The Top Threads Table

One row per conversation that had activity in the selected window, sorted by cost
(highest first, changeable). Each row shows:

- **Thread** — the conversation's ID (click to open its detail page) and which AI
  models it used
- **Agent** — which assistant the conversation was with
- **User** — who was chatting
- **Branch** — which environment the conversation ran in (production or a draft
  workspace)
- **Last activity** — when the conversation was last active
- **Messages** — how many responses the assistant generated
- **Tokens** — total tokens consumed
- **LLM cost / Warehouse cost / Total** — the two cost halves and their sum

Note: the table reflects only activity **within the selected window**. A long-running
conversation's lifetime cost lives on its detail page.

## The Thread Detail Page

Click any conversation to see its full lifetime breakdown.

### Cost Timeline

Each circle is one assistant response, placed on a time axis and **sized by how much it
cost** — big circles are expensive moments. Each circle is split like a pie: one color
for AI model cost, one for warehouse query cost. Hover over a circle for the exact
numbers. (Warehouse cost is matched to the response it most likely belongs to, so the
split is a close estimate rather than exact.)

### Summary Cards

- **Total cost** — AI model + warehouse cost for the whole conversation.
- **Total tokens** — lifetime token usage with the in/out/cache breakdown.
- **Messages** — how many responses the assistant generated, and which models served
  them.
- **Cache hit rate** — of the conversation history that *could* be served from cache,
  the share that actually was. **Higher is better.** A high rate means the conversation
  is being re-read at the heavily discounted cache price; a low rate on a long
  conversation means it's paying full price repeatedly.
- **LLM token cost / Warehouse compute cost** — the two halves on their own.

### Tools Used

When the assistant looks something up — runs a query, searches your knowledge base — it
uses a *tool*, and whatever the tool returns becomes part of the conversation the model
must read on **every following reply**. So one tool that returns a huge result keeps
costing money for the rest of the conversation.

This table lists each tool the assistant used, how often, and how much data it returned
in total (token counts here are estimates). It's sorted so the biggest data producer —
usually the biggest cost driver — is at the top.

### Heaviest Tool Calls

The individual lookups that returned the most data, with the response they happened on.
If one conversation got unexpectedly expensive, the culprit is usually visible here: a
single query that pulled back far more data than needed.

### Warehouse Queries

Every data query the assistant ran for this conversation, most expensive first, with
the query text, which warehouse ran it, how long it took, and what it cost. Like all
warehouse data, this can lag up to ~3 hours behind real time.

### Per-Turn Timeline

A row-by-row log of every assistant response: when it happened, which model answered,
the token breakdown (input, output, cache write, cache read), what that response cost,
and why the model stopped — it finished its answer, it paused to use a tool, or it hit
its output limit.

**How to read a healthy conversation:** later rows should show large *cache read*
numbers and small *input* numbers — the history is being served from cache cheaply. If
*input* stays large on every row, the conversation is re-paying full price for its
history each time.

## Common Questions

**Why is warehouse cost $0 for something that just ran?**
Snowflake's usage reporting lags about 3 hours. Check back later — the AI model cost is
already accurate.

**Why does a banner say warehouse cost is unavailable?**
The Snowflake role the platform uses needs read access to Snowflake's usage views. The
banner includes the exact permission your Snowflake admin should grant; AI model costs
are unaffected in the meantime.

**Why does a conversation's cost differ between the main page and its detail page?**
The main page counts only activity inside the selected date window; the detail page
shows the conversation's entire lifetime.

**Why don't the dollar totals exactly match my Snowflake invoice?**
Dollar figures use your configured per-credit rate, and a small portion of the AI
pricing (the cache rates) is a close approximation. For exact reconciliation, compare
the **credits** figures against Snowflake's billing reports — those are
rate-independent.

**What if a model shows a "missing rate card" warning?**
A new model was used before its pricing was registered, so its usage is temporarily
shown at $0. Token counts are still correct; contact Pliable support to update the
rate cards.
`,
};

export default doc;
